<template>
  <div v-bind:class="['screens', {'active': activeScreen}]">
    <div class="top">
      <div>
        <p class="title is-4">
          <slot></slot>
        </p>
        <LoadSave
          v-bind:mode="mode"
          v-bind:screens="screens"
          v-bind:screenSet="screenSet"
          v-bind:valuesSource="valuesSource"
          v-bind:isSaved="isSaved"
          v-bind:callback="saveCallback"
        />
        <!-- column -->
      </div><!-- columns -->
      <div class="filterflex first">
        <b-input placeholder="Search Screens"
          v-model="searchText"
          v-on:input="currentFilter = null"
          type="text"
          icon-pack="fas"
          icon="search"
          icon-right="times-circle"
          icon-right-clickable
          v-on:icon-right-click="clearSearch">
        </b-input>

        <div class="control">
          <button v-bind:class="layoutClass('grid')" v-on:click="layout = 'grid'">Grid</button>
          <button v-bind:class="layoutClass('tree')" v-on:click="layout = 'tree'">Tree</button>
        </div>
      </div>

      <div class="filterflex">
        <div class="control">
          <a v-bind:class="filterClass('applied')" v-on:click="applyFilter('applied')">Applied</a>
        </div>

        <div class="control">
          <a v-bind:class="filterClass('recommended')" v-on:click="applyFilter('recommended')">Top Issues</a>
        </div>

        <div class="control">
          <b-tooltip :label="positiveBinaryFilterLabel">
            <a v-bind:class="filterClass('positive-binary')" v-on:click="applyFilter('positive-binary')">
              <i class="fas fa-plus-circle"></i>
            </a>
          </b-tooltip>
        </div>

        <div class="control">
          <b-tooltip :label="negativeBinaryFilterLabel">
            <a v-bind:class="filterClass('negative-binary')" v-on:click="applyFilter('negative-binary')">
              <i class="fas fa-minus-circle"></i>
            </a>
          </b-tooltip>
        </div>

        <div class="control">
          <b-tooltip :label="thresholdFilterLabel">
            <a v-bind:class="filterClass('threshold')" v-on:click="applyFilter('threshold')">
              <i class="fas fa-sliders-h"></i>
            </a>
          </b-tooltip>
        </div>

        <div class="control" v-for="filter in filters">
          <b-tooltip v-bind:label="filter.name">
            <a v-bind:class="filterClass(filter.name)" v-on:click="applyFilter(filter.name)">
              <i v-bind:class="'fas ' + filter.icon" v-bind:title='filter.name'></i>
            </a>
          </b-tooltip>
        </div>

        <div class="control">
          <b-tooltip label="Show screens not applicable to current universe">
            <a v-bind:class="filterClass('not-applicable')" v-on:click="applyFilter('not-applicable')">N/A</a>
          </b-tooltip>
        </div>
      </div>
      <br>
    </div>

    <div class="body">
      <template v-if="!loading">
        <template v-if="filteredScreens.length">
          <ScreensTree
            v-if="layout === 'tree'"
            :screens="filteredScreens"
            :active-screen="activeScreen"
            :applicable-screens="applicableScreens"
            :mode="mode"
            @click="activate"
          />

          <div v-else class="card-list">
            <ScreenCard
              v-for="screen in filteredScreens"
              v-bind:key="screen.id"
              v-bind:screen="screen"
              v-bind:active="activeScreen && screen.id === activeScreen.id"
              v-bind:applicable="screen.id in applicableScreens"
              v-bind:mode="mode"
              v-on:click="activate(screen)"
            />
          </div>
        </template>

        <Callout v-else status="warning">
          <div class="no-screens">
            <p>No screens to display.</p>
            <p>Reset the filters or change universe.</p>
            <Buttons>
              <Button @click="searchText = ''; currentFilter = null;" variant="warning">Reset filters</Button>
              <EditUniverseButton variant="button" />
            </Buttons>
          </div>
        </Callout>
      </template>

      <template v-else>
        <b-loading />
      </template>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import ScreenCard from '@frontend/builder/js/components/ScreenCard.vue';
import ScreenDetailPane from '@frontend/builder/js/components/ScreenDetailPane.vue';
import LoadSave from '@frontend/builder/js/components/LoadSave.vue';
import ScreensTree from './ScreensTree.vue';
import EditUniverseButton from './EditUniverseButton.vue';
import { Button, Buttons, Callout } from '@stakeui';

import eventBus from '@frontend/shared/EventBus.js';
import gql from 'graphql-tag'
import telemetry from '@frontend/api/Telemetry';

export default {
  data() {
    return {
      currentFilter: 'recommended', // Filtered results are cached, you must call applyFilter() rather than mutating directly!
      activeScreen: null,
      isSaved: true,
      layout: 'grid',
      searchText: '',
      filters: [
        {name: 'Accountability', icon: 'fa-sun'},
        {name: 'Environment', icon: 'fa-globe'},
        {name: 'Equal Opportunity', icon: 'fa-road'},
        {name: 'Health', icon: 'fa-heartbeat'},
        {name: 'Human Rights', icon: 'fa-universal-access'},
      ],
      screenSet: {
        name: 'Untitled',
        id: null,
      },
      recommended: {
          'Animal Exploitation': true,
          'Bribery, Corruption, and Fraud Violations': true,
          'CEO Pay': true,
          'Deforestation Producers Exposure': true,
          'Environmental Violations': true,
          'Fossil Fuel Industry Exposure': true,
          'Migrant Detention Involvement': true,
          'Prison Industry Exposure': true,
          'Toxic Air Pollution': true,
          'Women on Boards': true,
      },
      appliedScreensCache: {},
      applicableScreens: {}
    }
  },
  apollo: {
    metrics: {
        query: gql`{
            metricDescription {
              edges { 
                node {
                  name
                  description
                  longDescription
                }
              }
            }
        }`,
        update(data) { 
        }
    }
  },
  props: {
    companies: Object,
    screens: Object,
    valuesSource: Object,
    mode: {
      type: String,
      default: 'screening',
      validator: function (value) { // Are we applying screens to a universe or just choosing them?
        return ['screening', 'selecting', 'hml'].indexOf(value) !== -1
      }
    }
  },
  components: {
    ScreenDetailPane,
    LoadSave,
    ScreenCard,
    ScreensTree,
    Button,
    Buttons,
    Callout,
    EditUniverseButton,
  },
  watch: {
    'companies.loaded':function() {
      console.log("Companies loaded watch triggered")
      if(!this.companies.loaded) {
        this.applicableScreens = this.screens.byId
      } else {
        this.applicableScreens = this.companies.a.reduce((acc, val) => {
        return Object.assign(acc, val.metrics.byId);
      }, {});
      }
    },
    screens: {
      immediate: true,
      handler(screens) {
        if(!this.companies.loaded) {
          this.applicableScreens = screens.byId
        }
      }
    }
  },
  computed: {
    loading: function() {
      return this.screens.a.length === 0;
    },
    lowercaseSearchText: function() {
      return this.searchText.toLowerCase() 
    },
    filteredScreens: function() {
      return this.screens.a.filter((itm) => {
        if (this.currentFilter !== 'not-applicable' && !(itm.id in this.applicableScreens) && itm.userPercentile === 0) {
          return false;
        }
        if (this.currentFilter === 'not-applicable') {
          return !(itm.id in this.applicableScreens);
        }
        if (itm.id in this.appliedScreensCache && this.currentFilter == 'applied') {
          return true;
        }
        if (!this.currentFilter && !this.searchText) {
          return true;
        }
        if (this.searchText) {
          let searchString = this.lowercaseSearchText;
          let lcaseItem = itm.name.toLowerCase()
          return (
            lcaseItem.indexOf(searchString) === 0
            || (searchString.length > 2 && lcaseItem.indexOf(searchString) !== -1)
          )
        }
        if (this.currentFilter === 'recommended') {
          return (itm.name in this.recommended);
        }
        else if (this.currentFilter === 'positive-binary') {
          return (itm.is_binary && itm.is_inclusionary);
        }
        else if (this.currentFilter === 'negative-binary') {
          return (itm.is_binary && !itm.is_inclusionary);
        }
        else if (this.currentFilter === 'threshold') {
          return (!itm.is_binary);
        }
        else {
          return (itm.subcategory[0].esgissue.name === this.currentFilter)
        }
      }).sort((a, b) => {
        let aIsSector = a.name.indexOf('Sector:') === 0;
        let bIsSector = b.name.indexOf('Sector:') === 0;
        if (aIsSector && !bIsSector) {
          return 1;
        }
        if (bIsSector && !aIsSector) {
          return -1;
        }

        return a.name > b.name ? 1 : -1;
      });
    },
    positiveBinaryFilterLabel() {
      const modeMessages = {
        screening: 'Positive binary metrics (selecting these metrics gives a green flag for all companies associated with these metrics)',
        selecting: 'Positive binary metrics (selecting these metrics gives a green flag for all companies associated with these metrics)',
        hml: 'Positive binary metrics (selecting these metrics gives a green flag for all companies associated with these metrics)'
      }
      return modeMessages[this.mode];
    },
    negativeBinaryFilterLabel() {
      const modeMessages = {
        screening: 'Negative binary metrics (selecting these metrics gives a red flag for all companies associated with these metrics)',
        selecting: 'Negative binary metrics (selecting these metrics gives a red flag for all companies associated with these metrics)',
        hml: 'Negative binary metrics (selecting these metrics gives a red flag for all companies associated with these metrics)',
      }
      return modeMessages[this.mode];
    },
    thresholdFilterLabel() {
      const modeMessages = {
        screening: 'Threshold metrics (flag companies with performance worse than a percentile threshold)',
        selecting: 'Threshold metrics (flag companies with performance worse than a percentile threshold)',
        hml: 'Threshold metrics (flag companies with performance worse than a percentile threshold)',
      }
      return modeMessages[this.mode];
    }
  },
  methods: {
    activate: function(screen, company) {
      if (this.activeScreen && this.activeScreen.id === screen.id) {
        this.activeScreen = null;
      }
      else {
        this.activeScreen = screen;
        this.activeScreen.prefilterFor = company;
      }
      this.$nextTick(() => {
        eventBus.$emit('reveal-screen-detail-pane', screen);
      });
      telemetry.track('Viewed Screen Details', {
        screen: screen.name,
        company: company ? company.name : null,
      });
    },
    deactivate: function() {
      this.activeScreen = null;
    },
    clearSearch: function() {
      this.searchText = '';
    },
    applyFilter: function(issueName) {
      if (this.currentFilter === issueName) {
        this.currentFilter = null;
        this.appliedScreensCache = {};
      }
      else {
        this.currentFilter = issueName;
      }

      if (this.currentFilter === 'applied') {
        // When filtering by applied screens we cache them so that they don't vanish when toggled, especially in the
        // case where a user is setting the slider to near-zero but accidentally hits 0. Screen cards will then only
        // disappear from the "Applied" category if they are set to 0/off before switching to another filter,
        // then back to applied.
        this.appliedScreensCache = this.screens.a.reduce((acc, itm) => {
          if (itm.userPercentile > 0) {
            acc[itm.id] = true;
          }
          return acc;
        }, {});
      }
    },
    filterClass: function(issueName) {
      let isActive = this.currentFilter === issueName ? 'is-primary' : '';
      return `button ${isActive}`;
    },
    layoutClass: function(name) {
      const isActive = this.layout === name ? 'is-primary' : '';
      return `button ${isActive}`;
    },
    onManuallySet: function(event, screen) {
      if (!screen.manuallySet) {
        screen.manuallySet = true;
      }
    },
    saveCallback: function(isSaved) {
      this.isSaved = isSaved;
    },
    handleLoadedScreenset(screenset, switchToApplied, isEdit) {

      this.isSaved = true;
      this.screenSet = JSON.parse(JSON.stringify(screenset));

      let newMetrics = screenset.metrics.reduce((acc, itm) => {
        acc[itm.metric_description] = itm.threshold;
        return acc;
      }, {});

      const resolveNullThreshold = (itm) => {
        if (itm.is_binary === true) return 1;
        const dbPct = itm.metric_meter_weighting?.user_percentile;
        if (typeof dbPct === 'number') return dbPct / 100;
        return 0.2;
      };

      this.screens.a.forEach((itm) => {
        itm.userPercentile = 0;
        itm.manuallySet = false;

        if (itm.id in newMetrics) {
          let value = newMetrics[itm.id];
          if (value === null) {
            value = resolveNullThreshold(itm);
            itm.manuallySet = false;
          } else {
            itm.manuallySet = !(
              value === 0 ||
              value === resolveNullThreshold(itm) ||
              (itm.is_binary && value === 1)
            );
          }
          itm.userPercentile = value * 100;
        }
      });
      const event = isEdit ? 'screens-changed-edit' : 'screens-changed';
      eventBus.$emit(event);

      if (switchToApplied && this.currentFilter !== 'applied') {
        this.applyFilter('applied');
      }

      let screenInfo = this.screens.a.map((itm) => ({
        metric_description: itm.id,
        threshold: itm.userPercentile,
      }));

      telemetry.track('Values Screenset Loaded', {
        screenset: screenset.name,
        screens: screenInfo,
      });
    },
  },
  created: function() {
    // Event listener for 'screen-selected'
    this.$root.$on('screen-selected', (screen, company) => {
      this.activate(screen, company);
    });

    // Event listener for 'screens-changed'
    eventBus.$on('screens-changed', () => {
      this.isSaved = false;

      let appliedScreens = this.screens.a
        .filter((itm) => itm.userPercentile > 0)
        .map((itm) => ({
          details: itm,
          metric_description: itm.id,
          threshold: itm.userPercentile / 100,
        }));

      this.$emit('set-update', appliedScreens);

      telemetry.track('Changed Screens', {
        'Screen Count': appliedScreens.length,
      });
    });
    eventBus.$on('screens-changed-edit', () => {
      this.isSaved = false;

      let appliedScreens = this.screens.a
        .filter((itm) => itm.userPercentile > 0)
        .map((itm) => ({
          details: itm,
          metric_description: itm.id,
          threshold: itm.userPercentile / 100,
        }));
    });

    // Event listener for 'screen-manually-set'
    eventBus.$on('screen-manually-set', (event, screen) => {
      this.onManuallySet(event, screen);
    });

    // Event listener for 'ScreenDetailPane:close'
    eventBus.$on('ScreenDetailPane:close', () => {
      this.activeScreen = null;
    });

    this.$root.$on('loaded-screenset', (screenset, switchToApplied) => {
      this.handleLoadedScreenset(screenset, switchToApplied, false);
    });

    this.$root.$on('loaded-screenset-edit', (screenset, switchToApplied) => {
      this.handleLoadedScreenset(screenset, switchToApplied, true);
    });
  },
}
</script>

<style lang="scss">
.screens {
  .title {
    margin-bottom: 0;
  }

  .card-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  column-gap: 1rem;
  row-gap: 1rem;
  }
  .esgcard {
    width: 15rem;
    height: 16rem;
    border: 1px solid;
    position: relative;
    flex-direction: column;
    justify-content: space-between;
    display: flex;
  }
  .esgcard.active {
    box-shadow: inset 0px 0px 18px 12px rgba(0,0,0,0.56);
  }
  .esgcard.not-applicable {
    opacity: 0.5;
    pointer-events: none;
  }
  .esgcard.not-applicable.applied {
    pointer-events: all;
  }
  .esgcard .title {
    cursor: pointer;
  }
  .b-slider .b-slider-tick-label {
    top: calc(1rem / 2 + 2px) !important;
    font-size: 1rem;
  }
  .esgissue {
    cursor: pointer;
  }
  .b-slider-track {
    background: silver;
  }
  .esgcard .b-slider.is-primary .b-slider-fill {
    background: whitesmoke !important;
  }
  .switch input[type=checkbox]:checked+.check {
    background: #71c3d2 !important;
  }
  .switch {
    position: relative;
    top: 15px;
    left: 10px;
  }
  .icon {
    cursor: pointer;
  }
  .notification {
    padding: 1.25rem 1.5rem 2.25rem 1.5rem !important;
  }
  .load-save.buttons  {
    & > div {
      margin-left: 15px;
    }
  }
  .filterflex {
    display: flex;
    justify-content: left;
    gap: 0.5rem;

    &.first {
      margin-bottom: 0.5rem;

      > .control:first-child {
        flex-grow: 1;
      }
    }
  }
}

.no-screens {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5em;
  padding: 1em;
  flex-direction: column;
}
</style>
